/** @jsx jsx */
import React, { useCallback, type PointerEvent, type FocusEvent } from 'react';
import { css, jsx } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Text } from '@atlaskit/primitives';
import { N800 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import EditorHorizontalRuleIcon from '@atlaskit/icon/glyph/editor/horizontal-rule';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import { Link, useRouterActions } from '@atlassian/react-resource-router';
import { BarChart as Bar } from '@atlassian/viz-platform-charts--next/charts';
import { element, useElementProps } from '@atlassian/viz-platform-charts--next/core';
import { useSectionAnalytics } from '../../../common/ui/analytics/index.tsx';
import { BarChart, type BarChartDatum } from '../../../common/ui/bar-chart/index.tsx';
import { ChartWithHeaderContainer } from '../../../common/ui/chart-with-header-container/index.tsx';
import { useSummaryFeatures } from '../../../controllers/features-context/index.tsx';
import { escapeFilterChars } from '../../utils.tsx';
import { BoundaryIds, SectionIds, type ColorScheme } from '../../../common/types.tsx';
import { useUFOCustomHoverData } from '../../../common/ui/ufo-custom-data-toggle/index.tsx';
import messages from './messages.tsx';

type PriorityBreakdownProps = {
	data: {
		issuesByPriority: {
			priority: { name: string; iconUrl: string; jqlTerm?: string };
			count: number;
		}[];
	};
	colorScheme?: ColorScheme;
};
const SUPPORT_DOC_URL =
	'https://support.atlassian.com/jira-cloud-administration/docs/configure-priorities-for-projects/';

const CustomTooltipHeader = element(
	({ onRender }: { onRender: () => void }) => {
		const { formatMessage } = useIntl();
		const { children, datum } = useElementProps(Bar.elements.TooltipHeader);
		onRender();

		return (
			<Bar.elements.TooltipHeader>
				{/* typed as unknown in viz library package */}
				{/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions */}
				{(datum as { jqlTerm?: string }).jqlTerm
					? children
					: formatMessage(messages.noPriorityLabel)}
			</Bar.elements.TooltipHeader>
		);
	},
	{ as: Bar.elements.TooltipHeader },
);

export const PriorityBreakdown = ({
	data: { issuesByPriority },
	colorScheme,
}: PriorityBreakdownProps) => {
	const { push } = useRouterActions();
	const { linksResolver, linksTarget } = useSummaryFeatures();
	const { formatMessage } = useIntl();
	const { addUFOCustomHoverData, UFOConditionalCustomHoverData } = useUFOCustomHoverData();

	const totalIssueCount = issuesByPriority.reduce((acc, { count }) => acc + count, 0);
	const { fireSectionAnalytics } = useSectionAnalytics(
		SectionIds.PriorityBreakdown,
		BoundaryIds.PriorityBreakdown,
	);

	const filteredList = useCallback(
		(jqlTerm?: string) =>
			linksResolver({
				filter: `priority = ${jqlTerm ? `'${escapeFilterChars(jqlTerm)}'` : 'EMPTY'}`,
				hideDone: true,
			}),
		[linksResolver],
	);

	const onTooltipClick = useCallback(
		({
			event,
			datum: { jqlTerm, count },
		}: {
			event?: PointerEvent | FocusEvent;
			datum: BarChartDatum;
		}) => {
			// this only gets emitted on a PointerEvent
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const buttonNumber = (event as PointerEvent)?.button;
			if (buttonNumber === 2) {
				// ignore right clicks
				return;
			}

			fireSectionAnalytics('viewWithFilter', { count, boundaryId: BoundaryIds.EpicProgress });
			const onClickHref = filteredList(jqlTerm);
			if (buttonNumber === 1) {
				// middle button should open in new tab
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.open(onClickHref, '_blank');
			} else if (linksTarget === '_self') {
				push(onClickHref);
			} else {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.open(onClickHref, linksTarget);
			}
		},
		[push, filteredList, fireSectionAnalytics, linksTarget],
	);

	const onLinkClick = useCallback(() => {
		fireSectionAnalytics('view');
	}, [fireSectionAnalytics]);

	return (
		<>
			<ChartWithHeaderContainer
				header={
					<>
						<Heading size="small" as="h2">
							<FormattedMessage {...messages.title} />
						</Heading>
						<Text>
							{totalIssueCount === 0 ? (
								<FormattedMessage
									{...messages.emptyDescription}
									values={{
										link: (chunks) => (
											<Link href={SUPPORT_DOC_URL} target="_blank" onClick={onLinkClick}>
												{chunks}
											</Link>
										),
									}}
								/>
							) : (
								<FormattedMessage
									{...messages.description}
									values={{
										link: (chunks) => (
											<Link
												href={linksResolver({
													sortBy: 'priority',
													isDescending: true,
													hideDone: true,
												})}
												target={linksTarget}
												onClick={onLinkClick}
											>
												{chunks}
											</Link>
										),
										totalIssueCount,
									}}
								/>
							)}
						</Text>
					</>
				}
				chart={
					<BarChart
						data={issuesByPriority.map((item) => ({
							name: item.priority.name,
							count: item.count,
							jqlTerm: item.priority.jqlTerm,
						}))}
						axisBottomTextTransformer={(children) => {
							const { jqlTerm, iconUrl } =
								issuesByPriority.find(({ priority: { name } }) => children === name)?.priority ||
								{};
							return (
								<Link
									href={filteredList(jqlTerm)}
									target={linksTarget}
									css={axixBottomTextLinkStyles}
								>
									<div css={axisBottomTextContainerStyles}>
										{iconUrl ? (
											<img css={iconStyles} alt="" src={iconUrl} />
										) : (
											<EditorHorizontalRuleIcon
												label=""
												size="small"
												primaryColor={token('color.text.subtle', '#7A869A')}
											/>
										)}

										<span css={axisBottomTextStyles}>
											{children ?? formatMessage(messages.noPriorityLabel)}
										</span>
									</div>
								</Link>
							);
						}}
						onTooltipClick={onTooltipClick}
						colorScheme={colorScheme}
					>
						<CustomTooltipHeader onRender={addUFOCustomHoverData} />
					</BarChart>
				}
			/>
			<UFOConditionalCustomHoverData />
		</>
	);
};

const axixBottomTextLinkStyles = css({
	// pointer-events is set to none in the library for the parent div
	// so we need to override it to make our link clickable
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	pointerEvents: 'auto !important',
	'&:hover': {
		textDecoration: 'none',
	},
});

const axisBottomTextContainerStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: token('space.050', '4px'),
});

const iconStyles = css({
	width: '16px',
	height: '16px',
});

const axisBottomTextStyles = css({
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	color: token('color.text.subtle', N800),
});
